import React, { useEffect, useState } from 'react'
import { useResolvedNav } from 'config/navigation/useResolvedNav'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'

import { ChevronLeft, ChevronRight } from 'components/lib/DesignSystem/Icons'

import Logo from '../topBar/Logo'

import CollapseToggle from './CollapseToggle'
import NavFlyout from './NavFlyout'
import NavSection from './NavSection'

// ── Constants ─────────────────────────────────────────────────────────────────

const EXPANDED_WIDTH = '288px'
const COLLAPSED_WIDTH = '60px'

// ── Styled components ─────────────────────────────────────────────────────────

const Rail = styled.nav`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: ${({ $collapsed }) => ($collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH)};
  /* ≤200ms transition per AC2 */
  transition: width 0.18s ease;
  background: var(--color-elevated-solid);
  overflow: hidden;
  flex-shrink: 0;
  box-sizing: border-box;
  position: relative;

  /* Right divider as a pseudo-element rather than border-right so it can stop
     below the top bar. Designer (2026-07-28, dark-mode question): after
     scrolling "the side and the top should look like 1 piece connected" — the
     scrolled bar takes the rail's own surface colour, so a divider crossing
     that band would slice the single surface in two. Below the bar the divider
     is still needed: there the rail meets the content area, which is a
     different colour in both themes.

     The offset is the FULL sticky-header height (--header-area-height, published
     by PlatformNavLayout), not just the top bar: when a global banner (e.g. demo
     mode) sits above the top bar the header is taller, and a fixed 52px offset
     retracted the divider next to the banner instead of the top bar — the
     separator glitch on scroll. Falls back to --top-bar-height when unmeasured. */
  &::after {
    content: '';
    position: absolute;
    top: ${({ $scrolled }) =>
      $scrolled ? 'var(--header-area-height, var(--top-bar-height, 52px))' : '0'};
    right: 0;
    bottom: 0;
    width: 1px;
    background: var(--color-border);
    pointer-events: none;
  }
`

// Nav-panel header — IRONSCALES logo + collapse toggle (Figma 1:26644: the logo
// lives here, not in the top bar). Expanded: logo left, toggle right. Collapsed:
// logo mark + toggle stacked, centered.
const Header = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'space-between')};
  flex-direction: ${({ $collapsed }) => ($collapsed ? 'column' : 'row')};
  padding: ${({ $collapsed }) => ($collapsed ? '10px 0 8px' : '10px 15px 8px')};
`

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  /* Collapsed uses the Figma 9px rail insets (60 − 9 − 9 = 42px, the icon-button
     width); expanded keeps the 12px content padding. The 30px top inset lands the
     first section further below the logo, per design review. */
  padding: ${({ $collapsed }) => ($collapsed ? '30px 9px 4px' : '30px 12px 4px')};
  display: flex;
  flex-direction: column;
  gap: 20px;

  /* Thin scrollbar */
  scrollbar-width: thin;
  scrollbar-color: var(--color-border) transparent;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border);
    border-radius: 4px;
  }
`

const SectionItem = styled.div``

// Divider between the active (entitled) sections and the locked/upsell sections,
// which are grouped at the bottom of the list (Figma "closed nav" — locked items
// sit under a separating line). Collapsed, the rule is inset to the icon column.
const LockedDivider = styled.hr`
  border: 0;
  height: 1px;
  background: var(--color-border);
  margin: ${({ $collapsed }) => ($collapsed ? '4px 6px' : '4px 0')};
`

// Right-edge hover collapse/expand affordance (Figma Make sidebar prototype):
// a thin full-height hit target on the rail's right edge. Idle it's invisible;
// on hover it washes a faint primary-blue gradient toward the border and
// surfaces a small floating chip with a directional chevron. Complements the
// header CollapseToggle — same onToggle, just a second, edge-hugging entry
// point — so it's hidden while the settings sub-view owns the rail.
const EdgeToggleChip = styled.span`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 6px;
  background: var(--primary);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;

  svg,
  svg path {
    fill: var(--sidebar-item-selected-foreground);
  }
`

const EdgeToggle = styled.button`
  all: unset;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 10px;
  cursor: pointer;
  box-sizing: border-box;
  z-index: 5;
  background: transparent;
  transition: background-color 0.2s ease;

  &:hover,
  &:focus-visible {
    background: linear-gradient(to left, rgba(48, 99, 253, 0.12), rgba(48, 99, 253, 0) 70%);
  }

  &:hover ${EdgeToggleChip}, &:focus-visible ${EdgeToggleChip} {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid var(--color-border);
    outline-offset: -2px;
  }
`

// Pinned-bottom slot for Settings (slice 3 mounts SettingsNav here)
const PinnedBottom = styled.div`
  flex-shrink: 0;
  padding: ${({ $collapsed }) => ($collapsed ? '6px 9px 12px' : '6px 12px 12px')};
  border-top: 1px solid var(--color-border);
`

// Settings sub-view body ("extended settings mode"): replaces the sections +
// pinned trigger below the rail header, so the IRONSCALES logo header stays put
// (matches the main menu). "Back to main menu" inside the panel restores the
// regular rail.
const SettingsBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--color-elevated-solid);

  animation: settingsOverlayOpen 0.15s ease-out;

  @keyframes settingsOverlayOpen {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * SideNav — left rail container.
 *
 * Slice-6 wires collapsed/onToggleCollapsed (persistence lives there).
 * Slice-3 passes settingsSlot (the pinned Settings area).
 *
 * @param {object} props
 * @param {boolean} props.collapsed - current collapsed state (owned by slice 6)
 * @param {() => void} props.onToggleCollapsed - called when the collapse toggle is clicked
 * @param {boolean} [props.collapseDisabled] - disable the in-rail toggle (Settings open)
 * @param {React.ReactNode} [props.settingsSlot] - Settings area rendered at the bottom (slice 3)
 * @param {React.ReactNode} [props.settingsPanel] - when set, the full settings
 *   sub-view: the rail renders at expanded width and this node covers it entirely
 *   ("extended settings mode"); pass null to show the regular rail
 * @param {string} [props.className]
 */
const SideNav = ({
  collapsed,
  onToggleCollapsed,
  collapseDisabled,
  settingsSlot,
  settingsPanel,
  className,
  scrolled = false,
}) => {
  const { pathname } = useLocation()
  const tree = useResolvedNav(undefined, pathname)

  // The settings sub-view always renders at full width. Closing it reveals the
  // width the parent passes in `collapsed`.
  const settingsOpen = Boolean(settingsPanel)
  const effectiveCollapsed = collapsed && !settingsOpen

  // Per-section expand/collapse state (open by default if section is active)
  const [expandedSections, setExpandedSections] = useState(() => {
    const initial = {}
    if (Array.isArray(tree)) {
      tree.forEach((node) => {
        if (node.type === 'section' && Array.isArray(node.children)) {
          initial[node.id] = node.active
        }
      })
    }
    return initial
  })

  const handleToggleExpand = (id) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  // The lazy initializer above only seeds expansion state once at mount. On SPA
  // navigation (deep link, logo→home jump, MSP company switch that changes the
  // active route) the resolved tree re-computes each section's `active` flag, so
  // re-sync here: auto-expand any section that becomes active. Only ever opens —
  // never collapses — so a user's manual expand/collapse on other sections is
  // preserved, and it re-fires only when the active set actually changes.
  const activeSectionIds = Array.isArray(tree)
    ? tree
        .filter((n) => n.type === 'section' && Array.isArray(n.children) && n.active)
        .map((n) => n.id)
    : []
  const activeSectionsKey = activeSectionIds.join('|')

  useEffect(() => {
    if (!activeSectionIds.length) return
    setExpandedSections((prev) => {
      let changed = false
      const next = { ...prev }
      activeSectionIds.forEach((id) => {
        if (!next[id]) {
          next[id] = true
          changed = true
        }
      })
      return changed ? next : prev
    })
    // activeSectionsKey captures the active section set; activeSectionIds is
    // derived from it each render (stable for a given key).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSectionsKey])

  // Split main sections from pinned-bottom nodes (Settings)
  const visibleNodes = Array.isArray(tree) ? tree.filter((n) => n.visible) : []
  const mainNodes = visibleNodes.filter((n) => !n.pinnedBottom)
  // pinnedBottom nodes are handed to settingsSlot (slice 3); we don't render them here

  // Locked (relevant-but-unentitled) sections group at the bottom, under a
  // divider (Figma "closed nav" — active features first, then a line, then the
  // locked upsell items). Order within each group is preserved.
  const activeNodes = mainNodes.filter((n) => !n.locked)
  const lockedNodes = mainNodes.filter((n) => n.locked)

  const renderSectionNode = (node) => {
    const nodeChildren = Array.isArray(node.children) ? node.children : []
    const isOpen = !!expandedSections[node.id]

    const sectionEl = (
      <NavSection
        id={node.id}
        label={node.label}
        icon={node.icon}
        resolvedPath={node.resolvedPath}
        active={node.active}
        locked={node.locked}
        plgPath={node.plg?.path}
        hasBellNotification={node.hasBellNotification}
        external={node.external}
        newTab={node.newTab}
        collapsed={effectiveCollapsed}
        open={isOpen}
        onToggleExpand={handleToggleExpand}
      >
        {nodeChildren}
      </NavSection>
    )

    // Wrap in NavFlyout when collapsed — locked sections skip the flyout wrap
    // (they already show the LockedNavItem upgrade tooltip, and a locked
    // section's children never surface independently, so a submenu flyout would
    // have nothing valid to show). Single popup per section: either the flyout
    // OR the locked-upgrade tooltip, never both.
    if (effectiveCollapsed && !node.locked) {
      return (
        <SectionItem key={node.id}>
          <NavFlyout
            collapsed={effectiveCollapsed}
            label={node.label}
            active={node.active}
            resolvedPath={node.resolvedPath}
            sectionId={node.id}
            hasBellNotification={node.hasBellNotification}
            trigger={sectionEl}
          >
            {nodeChildren}
          </NavFlyout>
        </SectionItem>
      )
    }

    return <SectionItem key={node.id}>{sectionEl}</SectionItem>
  }

  return (
    <Rail
      id="side-nav"
      $collapsed={effectiveCollapsed}
      $scrolled={scrolled}
      aria-label="Main navigation"
      className={className}
    >
      <Header $collapsed={effectiveCollapsed}>
        <Logo compact={effectiveCollapsed} />
        {/* Close control lives inside the open rail (logo row). When the rail
            is collapsed the expand control moves to the TopBar. */}
        {!effectiveCollapsed && onToggleCollapsed && (
          <CollapseToggle
            collapsed={false}
            onToggle={onToggleCollapsed}
            disabled={collapseDisabled}
            dataPendoFeature="nav-rail-collapse"
          />
        )}
      </Header>

      {settingsOpen ? (
        <SettingsBody>{settingsPanel}</SettingsBody>
      ) : (
        <>
          <ScrollArea $collapsed={effectiveCollapsed}>
            {activeNodes.map(renderSectionNode)}
            {lockedNodes.length > 0 && (
              <LockedDivider $collapsed={effectiveCollapsed} aria-hidden="true" />
            )}
            {lockedNodes.map(renderSectionNode)}
          </ScrollArea>

          {/* Pinned-bottom slot — slice 3 mounts SettingsNav here */}
          <PinnedBottom id="side-nav-settings-slot" $collapsed={effectiveCollapsed}>
            {settingsSlot}
          </PinnedBottom>

          {/* Edge hover-toggle */}
          <EdgeToggle
            type="button"
            onClick={onToggleCollapsed}
            data-pendo-feature="nav-rail-edge-toggle"
            aria-label={effectiveCollapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            <EdgeToggleChip aria-hidden="true">
              {effectiveCollapsed ? (
                <ChevronRight noDefaultFill fill="currentColor" width="10px" height="10px" />
              ) : (
                <ChevronLeft noDefaultFill fill="currentColor" width="10px" height="10px" />
              )}
            </EdgeToggleChip>
          </EdgeToggle>
        </>
      )}
    </Rail>
  )
}

export default SideNav
